import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { format } from 'date-fns';
import apiClient from '../api/client';

const API_BASE_URL = sessionStorage.getItem("API_BASE_URL");

function DeveloperDocsTab({ ticketId, ticketNumber, domain }) {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);

  const [showMetadataForm, setShowMetadataForm] = useState(false);
  const [metadataForm, setMetadataForm] = useState({
    ticketId: '',
    name: '',
    note: '',
    fileName: '',
    filePath: '',
    addedBy: '',
    addedOn: ''
  });

  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploadForm, setUploadForm] = useState({
    name: '',
    note: '',
    file: null
  });

  const token = localStorage.getItem('token');

  useEffect(() => {
    fetchDocs();
  }, [ticketId, domain, token]);

  const fetchDocs = async () => {
   try {
      const res = await apiClient.post(`/api/tickets/developer-documents`, {
        domain, ticketId: Number(ticketId) });
      setDocs(res.data || []);
    } catch (err) {
      toast.error('Failed to load developer documents');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleAddMetadata = async () => {
    if (!metadataForm.ticketId.trim()) {
      toast.error("Ticket ID is required");
      return;
    }
    if (!metadataForm.name.trim()) {
      toast.error("Name is required");
      return;
    }

   try {
      await apiClient.post(`/api/tickets/developer-documents/add`, {
        ...metadataForm, domain, ticketId: Number(metadataForm.ticketId.trim())
      });

      toast.success('Developer document metadata added');
      setShowMetadataForm(false);
      setMetadataForm({
        ticketId: '',
        name: '',
        note: '',
        fileName: '',
        filePath: '',
        addedBy: '',
        addedOn: ''
      });
      fetchDocs();
    } catch (err) {
      toast.error('Failed to add record');
      console.error(err.response?.data || err);
    }
  };

  const handleUpload = async () => {
    if (!uploadForm.file) return toast.error("File is required");
    if (!uploadForm.name.trim()) return toast.error("Name is required");

    const formData = new FormData();
    formData.append('file', uploadForm.file);
    formData.append('name', uploadForm.name.trim());
    formData.append('note', uploadForm.note.trim());
    formData.append('domain', domain);

    try {
      await apiClient.post(`/api/tickets/${ticketId}/developer-documents/upload`, formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });

      toast.success('Developer document uploaded');
      setShowUploadModal(false);
      setUploadForm({ name: '', note: '', file: null });
      fetchDocs();
    } catch (err) {
      toast.error('Upload failed');
      console.error(err.response?.data || err);
    }
  };

  const handleOpenFile = async (filePath, fileName) => {
    try {
      const res = await apiClient.get(`/api/files/open-path`, {
        params: { filePath, fileName }
      });
      window.open(res.data.url, '_blank');
    } catch (err) {
      toast.error('Failed to open file');
    }
  };

  if (loading) return <div className="text-center py-4">Loading developer documents...</div>;

  return (
    <div>

      <div className="mb-3">
        <button
          className="btn btn-theme btn-sm me-2"
          onClick={() => setShowMetadataForm(!showMetadataForm)}
        >
          {showMetadataForm ? 'Cancel' : 'Add new record'}
        </button>

        <button
          className="btn btn-primary btn-sm"
          onClick={() => setShowUploadModal(true)}
        >
          Upload Developer Document
        </button>
      </div>

      {showMetadataForm && (
        <div className="bg-white p-3 border rounded mb-3">

          <div className="row g-3 mb-3 align-items-end">
            <div className="col-md-2">
              <label className="form-label small fw-semibold mb-1">Ticket ID <span className="text-danger">*</span></label>
              <input
                type="text"
                className="form-control form-control-sm"
                placeholder="Enter Ticket ID"
                value={metadataForm.ticketId}
                onChange={e => setMetadataForm({ ...metadataForm, ticketId: e.target.value })}
              />
            </div>

            <div className="col-md-2">
              <label className="form-label small fw-semibold mb-1">Name <span className="text-danger">*</span></label>
              <input
                className="form-control form-control-sm"
                placeholder="Name"
                value={metadataForm.name}
                onChange={e => setMetadataForm({ ...metadataForm, name: e.target.value })}
              />
            </div>

            <div className="col-md-3">
              <label className="form-label small fw-semibold mb-1">Note</label>
              <input
                className="form-control form-control-sm"
                placeholder="Note"
                value={metadataForm.note}
                onChange={e => setMetadataForm({ ...metadataForm, note: e.target.value })}
              />
            </div>
            <div className="col-md-5">
              <label className="form-label small fw-semibold mb-1">File Name<span className="text-danger">*</span></label>
              <input
                className="form-control form-control-sm"
                placeholder="File Name"
                value={metadataForm.fileName}
                onChange={e => setMetadataForm({ ...metadataForm, fileName: e.target.value })}
              />
            </div>
          </div>

          <div className="row g-3 align-items-end">
            <div className="col-md-4">
              <label className="form-label small fw-semibold mb-1">File Path</label>
              <input
                className="form-control form-control-sm"
                placeholder="File Path (optional)"
                value={metadataForm.filePath}
                onChange={e => setMetadataForm({ ...metadataForm, filePath: e.target.value })}
              />
            </div>

            <div className="col-md-4">
              <label className="form-label small fw-semibold mb-1">Added By</label>
              <input
                className="form-control form-control-sm"
                placeholder="Added By"
                value={metadataForm.addedBy}
                onChange={e => setMetadataForm({ ...metadataForm, addedBy: e.target.value })}
              />
            </div>

            <div className="col-md-4">
              <label className="form-label small fw-semibold mb-1">Added On</label>
              <DatePicker
                selected={metadataForm.addedOn ? new Date(metadataForm.addedOn) : null}
                onChange={(date) => {
                  setMetadataForm({
                    ...metadataForm,
                    addedOn: date ? format(date, 'yyyy-MM-dd') : ''
                  });
                }}
                dateFormat="MM/dd/yyyy"
                placeholderText="MM/DD/YYYY"
                className="form-control form-control-sm"
                wrapperClassName="w-100"
                isClearable
                showMonthDropdown
                showYearDropdown
                dropdownMode="select"
              />
            </div>
          </div>

          <div className="mt-3 d-flex gap-2">
            <button className="btn btn-theme btn-sm px-3" onClick={handleAddMetadata}>
              Insert
            </button>
            <button className="btn btn-secondary btn-sm px-3" onClick={() => setShowMetadataForm(false)}>
              Cancel
            </button>
          </div>

        </div>
      )}

      {showUploadModal && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1050
          }}
          onClick={() => setShowUploadModal(false)}
        >
          <div
            className="bg-white p-4 border rounded shadow-lg"
            style={{ width: '500px', maxWidth: '90%' }}
            onClick={e => e.stopPropagation()}
          >
            <h5 className="mb-3">Specification Documents</h5>

            <div className="mb-3">
              <label className="form-label small fw-semibold">Name *</label>
              <input
                type="text"
                className="form-control form-control-sm"
                value={uploadForm.name}
                onChange={e => setUploadForm({ ...uploadForm, name: e.target.value })}
              />
            </div>

            <div className="mb-3">
              <label className="form-label small fw-semibold">Comments / Note</label>
              <textarea
                className="form-control form-control-sm"
                rows="4"
                value={uploadForm.note}
                onChange={e => setUploadForm({ ...uploadForm, note: e.target.value })}
              />
            </div>

            <div className="mb-3">
              <label className="form-label small fw-semibold">File Upload *</label>
              <input
                type="file"
                className="form-control form-control-sm"
                onChange={e => setUploadForm({ ...uploadForm, file: e.target.files[0] || null })}
              />
            </div>

            <div className="mt-4 text-end">
              <button
                className="btn btn-secondary btn-sm me-2"
                onClick={() => setShowUploadModal(false)}
              >
                Close
              </button>
              <button
                className="btn btn-theme btn-sm"
                onClick={handleUpload}
                disabled={!uploadForm.file || !uploadForm.name.trim()}
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}

      <table className="table table-sm table-bordered table-hover">
        <thead>
          <tr>
            <th>Ticket ID</th>
            <th>Name</th>
            <th>Note</th>
            <th>File Name</th>
            <th>File Path</th>
            <th>Added By</th>
            <th>Added On</th>
          </tr>
        </thead>
        <tbody>
          {docs.map((doc, i) => (
            <tr key={i}>
              <td>{doc.bg_ticket_id || '—'}</td>
              <td>{doc.sz_name || '—'}</td>
              <td>{doc.sz_note || '—'}</td>
              <td>
                {doc.sz_file_name ? (
                  <a
                    href="#"
                    onClick={(e) => {
                      e.preventDefault();
                      handleOpenFile(doc.sz_file_path, doc.sz_file_name);
                    }}
                    style={{ color: 'blue', textDecoration: 'underline', cursor: 'pointer' }}
                  >
                    {doc.sz_file_name}
                  </a>
                ) : '—'}
              </td>
              <td>{doc.sz_file_path || '—'}</td>
              <td>{doc.sz_uploaded_by || '—'}</td>
              <td>{doc.dt_created || '—'}</td>
            </tr>
          ))}
          {docs.length === 0 && (
            <tr>
              <td colSpan={7} className="text-center py-3">No developer documents found</td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

export default DeveloperDocsTab;